import React, { useEffect, useState } from "react";
import {
  Button,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Form,
  Card,
  notification,
  Select,
  Row,
  Col,
  Tooltip,
  Flex,
  DatePicker,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  FileTextOutlined,
  HistoryOutlined,
  PlusOutlined,
  ExportOutlined,
  EditOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import PageContentHeader from "../../../components/common/PageContentHeader";
import Title from "antd/es/skeleton/Title";
import Loader from "../../../components/common/Loader";
import {
  getBulkUploadTemplateService,
  getNewVehicleStockService,
  exportNewVehicleStockService,
  deleteVinService,
} from "../../../services/stock-services";
import {
  getAreasInStockService,
  getVehicleModelService,
} from "../../../services/master-services";
import { API_RESPONSE_CODES } from "../../../utills/constant";
import BulkUploadModal from "./BulkUploadModal";
import BulkUploadResultModal from "./BulkUploadResultModal";

const getDefaultStockFilters = () => ({
  physical_location: [],
  area_id: [],
  stock_status: null,
  model: null,
  blocked_status: null,
  delivery_status: null,
  delivery_denied: null,
  retail_status: null,
  oem_invoice_start_date: null,
  oem_invoice_end_date: null,
});

const StockList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isStockDashboardRedirect =
    searchParams.get("stock_dashboard_redirect") === "true";
  const roleType = useSelector((state) => state?.auth?.userData?.role);
  const isAdmin = useSelector((state) => state?.auth?.isAdmin);
  const permissions = useSelector(
    (state) => state?.auth?.userData?.permissions
  );
  const isBackOfficeManager = roleType === "Back Office Manager";
  // Check if user is in restricted roles (Back Office, Finance, Head Office)
  // Permission checks
  const hasAddPermission =
    isAdmin || permissions?.includes("add-new-vehicle-stock");
  const hasEditPermission =
    isAdmin || permissions?.includes("edit-new-vehicle-stock");
  const hasViewPermission =
    isAdmin || permissions?.includes("view-new-vehicle-stock");
  const hasDeletePermission =
    isAdmin || permissions?.includes("delete-new-vehicle-stock");
  const [deleteForm] = Form.useForm();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isResultOpen, setIsResultOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteResultOpen, setIsDeleteResultOpen] = useState(false);
  const [deleteResults, setDeleteResults] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);

  // Filter states
  const [filters, setFilters] = useState(getDefaultStockFilters);

  // Dropdown data states
  const [areaData, setAreaData] = useState([]);
  const [modelData, setModelData] = useState([]);

  // Fetch dropdown data
  const getAreaData = async () => {
    try {
      const areaDataArray = await getAreasInStockService(true);
      setAreaData(areaDataArray?.data?.data || []);
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    }
  };

  const getModelData = async () => {
    try {
      const modelDataArray = await getVehicleModelService(1); // Toyota make_id = 1
      setModelData(modelDataArray?.data?.data || []);
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    }
  };

  const fetchStockData = async (
    page = pagination.current,
    perPage = pagination.pageSize,
    searchTerm = search,
    filterParams = filters,
    options = {}
  ) => {
    const useDashboardRedirect =
      options.useDashboardRedirect !== undefined
        ? options.useDashboardRedirect
        : isStockDashboardRedirect;

    try {
      setLoading(true);
      const params = {
        page,
        per_page: perPage,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (useDashboardRedirect) {
        params.stock_dashboard_redirect = true;
        const stockDashboardCategory = searchParams.get("stock_dashboard_category");
        if (stockDashboardCategory) {
          params.stock_dashboard_category = stockDashboardCategory;
        }
        const matchedStockMonth = searchParams.get("matched_stock_month");
        if (matchedStockMonth) {
          params.matched_stock_month = matchedStockMonth;
        }
      }

      // Add filter parameters
      if (
        filterParams.physical_location &&
        filterParams.physical_location.length > 0
      ) {
        filterParams.physical_location.forEach((id, index) => {
          params[`physical_location_ids[${index}]`] = id;
        });
      }
      if (filterParams.area_id && filterParams.area_id.length > 0) {
        filterParams.area_id.forEach((id, index) => {
          params[`area_ids[${index}]`] = id;
        });
      }
      if (filterParams.stock_status) {
        params.stock_status = filterParams.stock_status;
      }
      if (filterParams.model) {
        params.vehicle_model_id = filterParams.model;
      }
      if (filterParams.blocked_status) {
        params.blocked_status = filterParams.blocked_status;
      }
      if (filterParams.delivery_status !== null && filterParams.delivery_status !== undefined) {
        params.delivery_status = filterParams.delivery_status;
      }
      if (filterParams.delivery_denied !== null && filterParams.delivery_denied !== undefined) {
        params.delivery_denied = filterParams.delivery_denied;
      }
      if (filterParams.retail_status !== null && filterParams.retail_status !== undefined) {
        params.retail_status = filterParams.retail_status;
      }
      if (filterParams.oem_invoice_start_date) {
        params.oem_invoice_start_date = filterParams.oem_invoice_start_date.format("YYYY-MM-DD");
      }
      if (filterParams.oem_invoice_end_date) {
        params.oem_invoice_end_date = filterParams.oem_invoice_end_date.format("YYYY-MM-DD");
      }

      const res = await getNewVehicleStockService(params);
      const list = res?.data?.data?.list_data || [];
      const total = res?.data?.data?.total_records ?? 0;

      const rows = Array.isArray(list)
        ? list.map((item) => ({
          key: item.id,
          id: item.id,
          chassis_number: item.chassis_number || "-",
          model_name: item.model_name || "-",
          variant_name: item.variant_name || "-",
          color_name: item.color_name || "-",
          fuel_type_name: item.fuel_type_name || "-",
          stock_status: item.stock_status || "-",
          physical_location_name: item.physical_location_name || "-",
          tkm_price: item.tkm_price || 0,
          wddp_date: item.wddp_date || "-",
          oem_invoice_date: item.oem_invoice_date || "-",
          delivery_date: item.delivery_date || "-",
          rto_date: item.rto_date || "-",
          mddp_month: item.mddp_month || "-",
          stock_age: item.stock_age || 0,
          allocation_status: item.allocation_status || "-",
          allocation_date: item.allocation_date || "-",
          allocation_age:
            item.allocation_age !== null && item.allocation_age !== undefined
              ? item.allocation_age
              : "-",
          blocked_status: item.blocked_status,
          blocked_reason: item.blocked_reason || "-",
          blocked_date: item.blocked_date || "-",
          blocking_age:
            item.blocking_age !== null && item.blocking_age !== undefined
              ? item.blocking_age
              : "-",
          co_dealer_purchase: item.co_dealer_purchase || "-",
          delivery_status: item.delivery_status || "-",
          delivery_denied: item.delivery_denied || "-",
          customer_name: item.customer_name || "-",
          customer_phone: item.customer_phone || "-",
          customer_area: item.customer_area || "-",
          sales_officer_name: item.sales_officer_name || "-",
          team_leader_name: item.team_leader_name || "-",
          booking_date: item.booking_date || "-",
          order_age:
            item.order_age !== null && item.order_age !== undefined
              ? item.order_age
              : "-",
          lead_status: item.lead_status || "-",
          ctdms_order_no: item.ctdms_order_no || "-",
          advanced_billing_name: item.advanced_billing_name || "-",
          retail_status: item.retail_status || "-",
          retail_date: item.retail_date || "-",
          retail_month: item.retail_month || "-",
          ex_showroom_price: item.ex_showroom_price || 0,
          registration_charges: item.registration_charges || 0,
          road_tax: item.road_tax || 0,
          rto_cost: item.rto_cost || 0,
          insurance_amount: item.insurance_amount || 0,
          insurance_company: item.insurance_company || "-",
          handling_charges: item.handling_charges || 0,
          extended_warranty: item.extended_warranty || 0,
          amc_amount: item.amc_amount || 0,
          accessories_amount: item.accessories_amount || 0,
          "3m_protection_amount": item["3m_protection_amount"] || 0,
          fastag: item.fastag || 0,
          tcs: item.tcs || 0,
          choice_number_charges: item.choice_number_charges || 0,
          other_logistic_charges: item.other_logistic_charges || 0,
          additional_discount: item.additional_discount || 0,
          other_oem_offer: item.other_oem_offer || 0,
          consumer_offer: item.consumer_offer || 0,
          discount_approval_insurance: item.discount_approval_insurance || 0,
          exchange_offer: item.exchange_offer || 0,
          corporate_offer: item.corporate_offer || 0,
          other_dealer_offer: item.other_dealer_offer || 0,
          on_road_price: item.on_road_price || 0,
          financier_name: item.financier_name || "-",
          in_house: item.in_house || "-",
          file_stage: item.file_stage || "-",
          seater_name: item.seater_name || "-",
          total_booking_amount: item.total_booking_amount || 0,
          total_down_payment_amount: item.total_down_payment_amount || "-",
          total_delivery_order_amount:
            item.total_delivery_order_amount || "-",
          total_disbursement_amount: item.total_disbursement_amount || "-",
          total_refund_amount: item.total_refund_amount || 0,
          total_payment_received: item.total_payment_received || 0,
          net_customer_balance: item.net_customer_balance || 0,
          document_collection_request_status:
            item.document_collection_request_status || "-",
          document_collection_head_office_manager_request_status:
            item.document_collection_head_office_manager_request_status ||
            "-",
          delivery_approval_sales_manager_request_status:
            item.delivery_approval_sales_manager_request_status || "-",
          delivery_approval_head_office_manager_request_status:
            item.delivery_approval_head_office_manager_request_status || "-",
          delivery_request_status: item.delivery_request_status || "-",
          cancellation_request_status:
            item.cancellation_request_status || "-",
          refund_processed: item.refund_processed || "-",
          gate_pass_generated: item.gate_pass_generated || "-",
          delivery_confirmation_documents:
            item.delivery_confirmation_documents || "-",
          update_vin: item.update_vin || false,
        }))
        : [];

      setData(rows);
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize: perPage,
        total: total,
      }));
    } catch (error) {
      notification.error({
        description: error?.response?.data?.message ?? error?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isRedirect = params.get("stock_dashboard_redirect") === "true";
    const stockDashboardCategory = params.get("stock_dashboard_category");
    const vehicleModelId = params.get("vehicle_model_id");
    const stockStatusFromQuery = params.get("stock_status");
    const physicalLocationIds = params
      .getAll("physical_location_ids[]")
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id));

    const stockRedirectWithoutModel = [
      "mtd_booking",
      "unmatched_mddp_stock_total",
      "unmatched_physical_stock_total",
      "unmatched_transit_stock_total",
      "unmatched_summary_total",
      "mddp_stock",
      "physical_stock",
      "transit_stock",
      "delivered",
      "matched",
      "retail_possibility_delivered",
      "retail_possibility_matched",
    ].includes(stockDashboardCategory);

    if (
      isRedirect &&
      stockDashboardCategory &&
      (vehicleModelId || stockRedirectWithoutModel)
    ) {
      let stockStatus = null;
      if (stockDashboardCategory === "physical_stock") stockStatus = "Physical";
      if (stockDashboardCategory === "mddp_stock") stockStatus = "MDDP";
      if (stockDashboardCategory === "transit_stock") stockStatus = "Transit";
      if (stockDashboardCategory === "unmatched_physical_stock_total")
        stockStatus = "Physical";
      if (stockDashboardCategory === "unmatched_mddp_stock_total")
        stockStatus = "MDDP";
      if (stockDashboardCategory === "unmatched_transit_stock_total")
        stockStatus = "Transit";
      if (stockDashboardCategory === "matched_physical_stock")
        stockStatus = "Physical";
      if (stockDashboardCategory === "matched_mddp_stock")
        stockStatus = "MDDP";
      if (stockDashboardCategory === "matched_transit_stock")
        stockStatus = "Transit";

      const redirectedFilters = {
        ...getDefaultStockFilters(),
        physical_location: physicalLocationIds,
        model: vehicleModelId ? Number(vehicleModelId) : null,
        stock_status: stockStatusFromQuery || stockStatus,
      };

      setFilters(redirectedFilters);
      setSearch("");
      fetchStockData(1, pagination.pageSize, "", redirectedFilters);
      return;
    }

    const clearedFilters = getDefaultStockFilters();
    setFilters(clearedFilters);
    setSearch("");
    getAreaData();
    getModelData();
    fetchStockData(1, pagination.pageSize, "", clearedFilters, {
      useDashboardRedirect: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const columns = [
    { title: "Chassis Number", dataIndex: "chassis_number", key: "chassis_number" },
    { title: "Model", dataIndex: "model_name", key: "model_name" },
    {
      title: "Variant / Suffix",
      dataIndex: "variant_name",
      key: "variant_name",
    },
    { title: "Color", dataIndex: "color_name", key: "color_name" },
    { title: "Fuel", dataIndex: "fuel_type_name", key: "fuel_type_name" },
    { title: "Seat", dataIndex: "seater_name", key: "seater_name" },
    {
      title: "Stock Status",
      dataIndex: "stock_status",
      key: "stock_status",
      render: (val) =>
        val && val !== "-" ? (
          <Tag
            color={
              val === "Physical"
                ? "green"
                : val === "Transit"
                  ? "blue"
                  : "orange"
            }
          >
            {val}
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Branch / Location",
      dataIndex: "physical_location_name",
      key: "physical_location_name",
    },
    {
      title: "TKM Price",
      dataIndex: "tkm_price",
      key: "tkm_price",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "WDDP Date",
      dataIndex: "wddp_date",
      key: "wddp_date",
      render: (text) => {
        if (!text || text === "-") return "-";
        return dayjs(text).format("DD/MM/YYYY");
      },
    },
    {
      title: "OEM Invoice Date",
      dataIndex: "oem_invoice_date",
      key: "oem_invoice_date",
      render: (text) => {
        if (!text || text === "-") return "-";
        return dayjs(text).format("DD/MM/YYYY");
      },
    },
    {
      title: "Delivery Date",
      dataIndex: "delivery_date",
      key: "delivery_date",
      render: (text) => {
        if (!text || text === "-") return "-";
        return dayjs(text).format("DD/MM/YYYY");
      },
    },
    {
      title: "RTO Date",
      dataIndex: "rto_date",
      key: "rto_date",
      render: (text) => {
        if (!text || text === "-") return "-";
        return dayjs(text).format("DD/MM/YYYY");
      },
    },
    { title: "MDDP Month", dataIndex: "mddp_month", key: "mddp_month" },
    { title: "Age (days in stock)", dataIndex: "stock_age", key: "stock_age" },
    {
      title: "Allocation Status",
      dataIndex: "allocation_status",
      key: "allocation_status",
    },
    {
      title: "Allocation Date",
      dataIndex: "allocation_date",
      key: "allocation_date",
      render: (text) => {
        if (!text || text === "-") return "-";
        return dayjs(text).format("DD/MM/YYYY");
      },
    },
    {
      title: "Allocation Age",
      dataIndex: "allocation_age",
      key: "allocation_age",
    },
    {
      title: "Blocked Status",
      dataIndex: "blocked_status",
      key: "blocked_status",
      render: (val) => (val ? <Tag color="red">{val}</Tag> : "-"),
    },
    {
      title: "Blocked Reason",
      dataIndex: "blocked_reason",
      key: "blocked_reason",
    },
    {
      title: "Blocked Date",
      dataIndex: "blocked_date",
      key: "blocked_date",
      render: (text) => {
        if (!text || text === "-") return "-";
        return dayjs(text).format("DD/MM/YYYY");
      },
    },
    { title: "Blocking Age", dataIndex: "blocking_age", key: "blocking_age" },
    {
      title: "Co-dealer Purchase",
      dataIndex: "co_dealer_purchase",
      key: "co_dealer_purchase",
    },
    {
      title: "Delivery Status",
      dataIndex: "delivery_status",
      key: "delivery_status",
    },
    {
      title: "Delivery Denied Flag",
      dataIndex: "delivery_denied",
      key: "delivery_denied",
    },
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
    },
    ...(isBackOfficeManager
      ? []
      : [
        {
          title: "Contact No.",
          dataIndex: "customer_phone",
          key: "customer_phone",
        },
      ]),
    {
      title: "Area of Booking",
      dataIndex: "customer_area",
      key: "customer_area",
    },
    {
      title: "Sales Officer Name",
      dataIndex: "sales_officer_name",
      key: "sales_officer_name",
    },
    {
      title: "Team Lead Name",
      dataIndex: "team_leader_name",
      key: "team_leader_name",
    },
    {
      title: "Date of Booking",
      dataIndex: "booking_date",
      key: "booking_date",
      render: (text) => {
        if (!text || text === "-") return "-";
        return dayjs(text).format("DD/MM/YYYY");
      },
    },
    { title: "Order Age", dataIndex: "order_age", key: "order_age" },
    { title: "Lead Status", dataIndex: "lead_status", key: "lead_status" },
    {
      title: "CTDMS Order No.",
      dataIndex: "ctdms_order_no",
      key: "ctdms_order_no",
    },
    {
      title: "Advanced Billing Name",
      dataIndex: "advanced_billing_name",
      key: "advanced_billing_name",
    },
    {
      title: "Retail Status",
      dataIndex: "retail_status",
      key: "retail_status",
    },
    {
      title: "Retail Date",
      dataIndex: "retail_date",
      key: "retail_date",
      render: (text) => {
        if (!text || text === "-") return "-";
        return dayjs(text).format("DD/MM/YYYY");
      },
    },
    {
      title: "Retail Month",
      dataIndex: "retail_month",
      key: "retail_month",
      render: (text) => {
        if (!text || text === "-") return "-";
        const parsed = dayjs(text, "MM-YYYY", true);
        return parsed.isValid() ? parsed.format("MMM-YYYY") : text;
      },
    },
    {
      title: "Ex-Showroom Price",
      dataIndex: "ex_showroom_price",
      key: "ex_showroom_price",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Registration Charges",
      dataIndex: "registration_charges",
      key: "registration_charges",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Road Tax Amount",
      dataIndex: "road_tax",
      key: "road_tax",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "RTO Cost",
      dataIndex: "rto_cost",
      key: "rto_cost",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Insurance Amount",
      dataIndex: "insurance_amount",
      key: "insurance_amount",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Insurance Company",
      dataIndex: "insurance_company",
      key: "insurance_company",
    },
    {
      title: "Handling Charges",
      dataIndex: "handling_charges",
      key: "handling_charges",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Extended Warranty (EW)",
      dataIndex: "extended_warranty",
      key: "extended_warranty",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "AMC Amount",
      dataIndex: "amc_amount",
      key: "amc_amount",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Accessories (DLC Amt)",
      dataIndex: "accessories_amount",
      key: "accessories_amount",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "3M Protection Amount",
      dataIndex: "3m_protection_amount",
      key: "3m_protection_amount",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Fastag",
      dataIndex: "fastag",
      key: "fastag",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "TCS",
      dataIndex: "tcs",
      key: "tcs",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Choice Number Charges",
      dataIndex: "choice_number_charges",
      key: "choice_number_charges",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Other Logistics Charges",
      dataIndex: "other_logistic_charges",
      key: "other_logistic_charges",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Additional Discount",
      dataIndex: "additional_discount",
      key: "additional_discount",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Other OEM Offer",
      dataIndex: "other_oem_offer",
      key: "other_oem_offer",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Consumer Offer",
      dataIndex: "consumer_offer",
      key: "consumer_offer",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Discount Approval Insurance",
      dataIndex: "discount_approval_insurance",
      key: "discount_approval_insurance",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Exchange Offer",
      dataIndex: "exchange_offer",
      key: "exchange_offer",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Corporate Offer",
      dataIndex: "corporate_offer",
      key: "corporate_offer",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Other Dealer Offer",
      dataIndex: "other_dealer_offer",
      key: "other_dealer_offer",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "On-road Value",
      dataIndex: "on_road_price",
      key: "on_road_price",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Financier Name",
      dataIndex: "financier_name",
      key: "financier_name",
    },
    { title: "In-house Finance", dataIndex: "in_house", key: "in_house" },
    { title: "File Stage", dataIndex: "file_stage", key: "file_stage" },
    {
      title: "Total Booking Amount",
      dataIndex: "total_booking_amount",
      key: "total_booking_amount",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Total Down Payment Amount",
      dataIndex: "total_down_payment_amount",
      key: "total_down_payment_amount",
    },
    {
      title: "Total Delivery Order (DO) Amount",
      dataIndex: "total_delivery_order_amount",
      key: "total_delivery_order_amount",
    },
    {
      title: "Total Disbursement Amount",
      dataIndex: "total_disbursement_amount",
      key: "total_disbursement_amount",
    },
    {
      title: "Total Refund Amount",
      dataIndex: "total_refund_amount",
      key: "total_refund_amount",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Total Payment Received",
      dataIndex: "total_payment_received",
      key: "total_payment_received",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Net Customer Balance",
      dataIndex: "net_customer_balance",
      key: "net_customer_balance",
      render: (val) =>
        val && val !== 0 ? `₹${val.toLocaleString("en-IN")}` : "-",
    },
    {
      title: "Document Collection Request Status",
      dataIndex: "document_collection_request_status",
      key: "document_collection_request_status",
    },
    // {
    //   title: "Document Collection Request Status (Head Office)",
    //   dataIndex: "document_collection_head_office_manager_request_status",
    //   key: "document_collection_head_office_manager_request_status",
    // },
    {
      title: "Delivery Approval (Sales Manager)",
      dataIndex: "delivery_approval_sales_manager_request_status",
      key: "delivery_approval_sales_manager_request_status",
    },
    {
      title: "Delivery Approval (Head Office)",
      dataIndex: "delivery_approval_head_office_manager_request_status",
      key: "delivery_approval_head_office_manager_request_status",
    },
    {
      title: "Delivery Request Status",
      dataIndex: "delivery_request_status",
      key: "delivery_request_status",
    },
    {
      title: "Cancellation Request Status",
      dataIndex: "cancellation_request_status",
      key: "cancellation_request_status",
    },
    {
      title: "Refund Processed",
      dataIndex: "refund_processed",
      key: "refund_processed",
    },
    {
      title: "Gate Pass Generated",
      dataIndex: "gate_pass_generated",
      key: "gate_pass_generated",
      render: (text) => {
        if (!text || text === "-") return "-";
        return dayjs(text).format("DD/MM/YYYY");
      },
    },
    {
      title: "Delivery Confirmation Documents",
      dataIndex: "delivery_confirmation_documents",
      key: "delivery_confirmation_documents",
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 100,
      render: (_, row) => {
        // If no view permission, don't show actions
        if (!hasViewPermission) {
          return "-";
        }

        return (
          <Space>
            {hasViewPermission && (
              <Tooltip title="View Stock">
                <Button
                  shape="circle"
                  icon={<EyeOutlined />}
                  onClick={() => {
                    if (isStockDashboardRedirect) {
                      window.open(
                        `${window.location.origin}/admin/stock/view-vin/${row.id}`,
                        "_blank",
                        "noopener,noreferrer"
                      );
                    } else {
                      navigate(`/admin/stock/view-vin/${row.id}`);
                    }
                  }}
                />
              </Tooltip>
            )}
            {!isStockDashboardRedirect && hasEditPermission && row.update_vin && row.delivery_status !== "Delivered" && (
              <Tooltip title="Edit Stock">
                <Button
                  shape="circle"
                  icon={<EditOutlined />}
                  onClick={() =>
                    navigate(
                      `/admin/stock/edit-vin/${row.id}/${row.chassis_number}`
                    )
                  }
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const values = await deleteForm.validateFields();

      setIsDeleting(true);

      const deletedVinIds = selectedRowKeys
        .map((key) => {
          const item = data.find((d) => d.key === key);
          return item?.id;
        })
        .filter((id) => id !== undefined);

      if (deletedVinIds.length === 0) {
        notification.error({
          description: "No valid VIN IDs found to delete",
        });
        setIsDeleting(false);
        return;
      }

      const payload = {
        deleted_vin_ids: deletedVinIds,
        remarks: values.reason.trim(),
      };

      const response = await deleteVinService(payload);

      if (
        response?.data?.status === API_RESPONSE_CODES.OK ||
        response?.data?.status === API_RESPONSE_CODES.NO_CONTENT
      ) {
        setIsDeleting(false);
        setIsDeleteModalOpen(false);
        deleteForm.resetFields();

        // Process and display results
        const results = response?.data?.data || [];
        const formattedResults = results.map((item) => ({
          key: item.chassis_number,
          vin: item.chassis_number,
          status: item.status === "success" ? "Success" : "Failed",
          reason: item.failure_reason || "-",
        }));

        setDeleteResults(formattedResults);
        setIsDeleteResultOpen(true);
        setSelectedRowKeys([]);
        fetchStockData(
          pagination.current,
          pagination.pageSize,
          search,
          filters
        );
      } else {
        // Handle unexpected response status
        setIsDeleting(false);
        notification.error({
          description: "Unexpected response from server",
        });
      }
    } catch (error) {
      if (error.errorFields) {
        // Form validation error - don't show notification, form will show error
        setIsDeleting(false);
        return;
      }
      notification.error({
        description:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to delete VIN(s)",
      });
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    deleteForm.resetFields();
  };

  const handleDeleteResultClose = () => {
    setIsDeleteResultOpen(false);
    setDeleteResults([]);
  };

  const handleDownloadTemplate = async () => {
    try {
      setIsDownloadingTemplate(true);
      const response = await getBulkUploadTemplateService();

      if (response.data.status === 200 && response.data.data?.path) {
        // Create a temporary link element and trigger download
        const link = document.createElement("a");
        link.href = response.data.data.path;
        link.target = "_blank";
        link.download = "new_vehicle_stock_template.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        notification.success({
          message: response.data.message || "Template downloaded successfully",
        });
      }
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const params = {};

      if (search) {
        params.search = search;
      }

      // Add filter parameters to export
      if (filters.physical_location && filters.physical_location.length > 0) {
        filters.physical_location.forEach((id, index) => {
          params[`physical_location_ids[${index}]`] = id;
        });
      }
      if (filters.area_id && filters.area_id.length > 0) {
        filters.area_id.forEach((id, index) => {
          params[`area_ids[${index}]`] = id;
        });
      }
      if (filters.stock_status) {
        params.stock_status = filters.stock_status;
      }
      if (filters.model) {
        params.vehicle_model_id = filters.model;
      }
      if (filters.blocked_status) {
        params.blocked_status = filters.blocked_status;
      }
      if (filters.delivery_status !== null && filters.delivery_status !== undefined) {
        params.delivery_status = filters.delivery_status;
      }
      if (filters.delivery_denied !== null && filters.delivery_denied !== undefined) {
        params.delivery_denied = filters.delivery_denied;
      }
      if (filters.retail_status !== null && filters.retail_status !== undefined) {
        params.retail_status = filters.retail_status;
      }
      if (filters.oem_invoice_start_date) {
        params.oem_invoice_start_date = filters.oem_invoice_start_date.format("YYYY-MM-DD");
      }
      if (filters.oem_invoice_end_date) {
        params.oem_invoice_end_date = filters.oem_invoice_end_date.format("YYYY-MM-DD");
      }

      const response = await exportNewVehicleStockService(params);

      // The API returns a path URL in the response
      if (response?.data?.status === 200 && response?.data?.data?.path) {
        // Create a temporary link element and trigger download
        const link = document.createElement("a");
        link.href = response.data.data.path;
        link.download = `new_vehicle_stock_${new Date().getTime()}.xls`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        notification.success({
          message: response.data.message || "Stock data exported successfully",
        });
      } else {
        notification.error({
          description: "Export file path not found in response",
        });
      }
    } catch (error) {
      notification.error({
        description: error.response?.data?.message ?? error?.message,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    fetchStockData(1, pagination.pageSize, value?.trim(), filters);
  };

  const handleFilterChange = (filterName, value) => {
    // Date validation for OEM Invoice dates
    if (filterName === "oem_invoice_start_date") {
      if (value && filters.oem_invoice_end_date && value.isAfter(filters.oem_invoice_end_date)) {
        notification.error({
          description: "Start Date cannot be after End Date.",
        });
        return;
      }
    }
    if (filterName === "oem_invoice_end_date") {
      if (value && filters.oem_invoice_start_date && value.isBefore(filters.oem_invoice_start_date)) {
        notification.error({
          description: "End Date cannot be before Start Date.",
        });
        return;
      }
    }

    const newFilters = {
      ...filters,
      [filterName]: value,
    };
    setFilters(newFilters);
    // Reset to page 1 when filter changes
    fetchStockData(1, pagination.pageSize, search, newFilters);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: () => ({
      disabled: !hasDeletePermission,
    }),
  };

  const handleBulkUploadSuccess = (results) => {
    setUploadResults(results);
    setIsResultOpen(true);
    // Refresh stock data after successful upload
    fetchStockData(pagination.current, pagination.pageSize, search, filters);
  };

  const handleCloseResultModal = () => {
    setIsResultOpen(false);
    setUploadResults(null);
  };

  const stockRedirectTags = [];
  if (isStockDashboardRedirect) {
    const stockDashboardCategory = searchParams.get("stock_dashboard_category");
    if (stockDashboardCategory) {
      const categoryLabelMap = {
        physical_stock: "Physical Stock",
        mddp_stock: "MDDP Stock",
        transit_stock: "Transit Stock",
        unmatched_summary_total: "Unmatched Summary Total",
        unmatched_physical_stock_total: "Unmatched Physical Stock Total",
        unmatched_mddp_stock_total: "Unmatched MDDP Stock Total",
        unmatched_transit_stock_total: "Unmatched Transit Stock Total",
        mtd_booking: "MTD Booking",
        matched_physical_stock: "Matched Physical Stock",
        matched_mddp_stock: "Matched MDDP Stock",
        matched_transit_stock: "Matched Transit Stock",
        delivered: "Delivered",
        matched: "Matched",
        retail_possibility_delivered: "Retail Possibility Delivered",
        retail_possibility_matched: "Retail Possibility Matched",
      };
      stockRedirectTags.push([
        "Category",
        categoryLabelMap[stockDashboardCategory] || stockDashboardCategory,
      ]);
    }
    if (filters.stock_status) {
      stockRedirectTags.push(["Stock Status", filters.stock_status]);
    }
    if (filters.model) {
      const modelName =
        searchParams.get("vehicle_model_name") ||
        modelData?.find((model) => String(model.id) === String(filters.model))
          ?.model_name ||
        filters.model;
      stockRedirectTags.push(["Model", modelName]);
    }
    if (filters.physical_location?.length) {
      const locationNames =
        searchParams.get("physical_location_names") ||
        filters.physical_location
          .map(
            (id) =>
              areaData?.find((area) => String(area.id) === String(id))?.name ||
              id
          )
          .join(", ");
      stockRedirectTags.push(["Physical Location", locationNames]);
    }
    if (searchParams.get("matched_stock_month")) {
      stockRedirectTags.push([
        "Matched Stock Month",
        searchParams.get("matched_stock_month"),
      ]);
    }
  }

  const handleGoToStockList = () => {
    navigate("/admin/stock", { replace: true });
  };

  return (
    <div className="user-data">
      <Loader loading={loading} />
      {!isStockDashboardRedirect && <PageContentHeader title="Stock - New Cars" />}
      <div className="personal-detail">
        {!isStockDashboardRedirect && (
          <div className="user-left d-flex align-items-center">
            <Title level={4} className="pe-2 pb-2">
              Stock - New Cars
            </Title>
          </div>
        )}
        {!isStockDashboardRedirect && (
          <Card className="filter-card mb-3">
            <Form layout="vertical">
              {/* Filters Row - explicit xl/lg for stable alignment at 1920x1080 */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={24} md={4} lg={4} xl={4}>
                <Form.Item name="search" label="Search (Chassis,Model,Suffix)">
                  <Input.Search
                    placeholder="Search"
                    allowClear
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onSearch={handleSearch}
                    onPressEnter={(e) => handleSearch(e.target.value)}
                    className="form-control-ant"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4} lg={4} xl={4}>
                <Form.Item label="Physical Location" className="mb-0">
                  <Select
                    mode="multiple"
                    className="form-control-ant"
                    placeholder="Select Physical Location"
                    allowClear
                    value={filters.physical_location}
                    onChange={(value) =>
                      handleFilterChange("physical_location", value)
                    }
                    style={{ width: "100%" }}
                  >
                    {areaData?.map((area) => (
                      <Select.Option key={area.id} value={area.id}>
                        {area.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4} lg={4} xl={4}>
                <Form.Item label="Area of Booking" className="mb-0">
                  <Select
                    mode="multiple"
                    className="form-control-ant"
                    placeholder="Select Areas"
                    allowClear
                    value={filters.area_id}
                    onChange={(value) => handleFilterChange("area_id", value)}
                    style={{ width: "100%" }}
                  >
                    {areaData?.map((area) => (
                      <Select.Option key={area.id} value={area.id}>
                        {area.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4} lg={4} xl={4}>
                <Form.Item label="Stock Status" className="mb-0">
                  <Select
                    className="form-control-ant"
                    placeholder="Select Stock Status"
                    allowClear
                    value={filters.stock_status}
                    onChange={(value) =>
                      handleFilterChange("stock_status", value)
                    }
                    style={{ width: "100%" }}
                  >
                    <Select.Option value="Physical">Physical</Select.Option>
                    <Select.Option value="Transit">Transit</Select.Option>
                    <Select.Option value="MDDP">MDDP</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4} lg={4} xl={4}>
                <Form.Item label="Model" className="mb-0">
                  <Select
                    className="form-control-ant"
                    placeholder="Select Model"
                    allowClear
                    value={filters.model}
                    onChange={(value) => handleFilterChange("model", value)}
                    style={{ width: "100%" }}
                  >
                    {modelData?.map((model) => (
                      <Select.Option key={model.id} value={model.id}>
                        {model.model_name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4} lg={4} xl={4}>
                <Form.Item label="Block Status" className="mb-0">
                  <Select
                    className="form-control-ant"
                    placeholder="Select Block Status"
                    allowClear
                    value={filters.blocked_status}
                    onChange={(value) =>
                      handleFilterChange("blocked_status", value)
                    }
                    style={{ width: "100%" }}
                  >
                    <Select.Option value="Yes">Yes</Select.Option>
                    <Select.Option value="No">No</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4} lg={4} xl={4}>
                <Form.Item label="Delivery Status" className="mb-0">
                  <Select
                    className="form-control-ant"
                    placeholder="Select Delivery Status"
                    allowClear
                    value={filters.delivery_status}
                    onChange={(value) =>
                      handleFilterChange("delivery_status", value)
                    }
                  >
                    <Select.Option value={0}>Not Delivered</Select.Option>
                    <Select.Option value={1}>Delivered</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4} lg={4} xl={4}>
                <Form.Item label="Delivery Denied" className="mb-0">
                  <Select
                    className="form-control-ant"
                    placeholder="Select Delivery Denied"
                    allowClear
                    value={filters.delivery_denied}
                    onChange={(value) =>
                      handleFilterChange("delivery_denied", value)
                    }
                    style={{ width: "100%" }}
                  >
                    <Select.Option value="1">Yes</Select.Option>
                    <Select.Option value="0">No</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4} lg={4} xl={4}>
                <Form.Item label="Retail Status" className="mb-0">
                  <Select
                    className="form-control-ant"
                    placeholder="Select Retail Status"
                    allowClear
                    value={filters.retail_status}
                    onChange={(value) =>
                      handleFilterChange("retail_status", value)
                    }
                    style={{ width: "100%" }}
                  >
                    <Select.Option value="1">Retailed</Select.Option>
                    <Select.Option value="0">Not Retailed</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4} lg={4} xl={4}>
                <Form.Item label="OEM Invoice Start Date" className="mb-0">
                  <DatePicker
                    format="DD/MM/YYYY"
                    className="form-control-ant"
                    placeholder="Select Start Date"
                    allowClear
                    value={filters.oem_invoice_start_date}
                    onChange={(date) =>
                      handleFilterChange("oem_invoice_start_date", date)
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4} lg={4} xl={4}>
                <Form.Item label="OEM Invoice End Date" className="mb-0">
                  <DatePicker
                    format="DD/MM/YYYY"
                    className="form-control-ant"
                    placeholder="Select End Date"
                    allowClear
                    value={filters.oem_invoice_end_date}
                    onChange={(date) =>
                      handleFilterChange("oem_invoice_end_date", date)
                    }
                    disabledDate={(current) => {
                      if (filters.oem_invoice_start_date) {
                        return current && current < filters.oem_invoice_start_date.startOf("day");
                      }
                      return false;
                    }}
                  />
                </Form.Item>
              </Col>
              {/* Spacer so second row aligns with first (6-column grid from md up) */}
              <Col xs={0} sm={0} md={4} lg={4} xl={4} />
              </Row>

              {/* Search and Action Buttons Row */}
            </Form>
          </Card>
        )}
        <Card>
          {isStockDashboardRedirect && stockRedirectTags.length > 0 && (
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
              <Space size="middle" wrap>
                {stockRedirectTags.map(([key, value]) => (
                  <Space key={key}>
                    <span className="tag-label">{key}:</span>
                    <Tag color="green">{value}</Tag>
                  </Space>
                ))}
              </Space>
              <Button
                type="primary"
                className="secondary-button"
                onClick={handleGoToStockList}
              >
                Go to Stock List
              </Button>
            </div>
          )}
          <Flex
            align="center"
            justify="end"
            className="flex-wrap mb-3 justify-content-md-start "
            gap={16}
          >
            {hasAddPermission && !isStockDashboardRedirect && (
              <>
                <Button
                  icon={<FileTextOutlined />}
                  style={{
                    backgroundColor: "#0a9551",
                    borderColor: "#0a9551",
                    color: "#fff",
                    fontWeight:'500'
                  }}
                  onClick={handleDownloadTemplate}
                  loading={isDownloadingTemplate}
                >
                  Template
                </Button>
                <Button
                  icon={<UploadOutlined />}
                  style={{
                    backgroundColor: "#0a9551",
                    borderColor: "#0a9551",
                    color: "#fff",
                                        fontWeight:'500'

                  }}
                  onClick={() => setIsUploadOpen(true)}
                >
                  Bulk Update
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  style={{
                    backgroundColor: "#0a9551",
                    borderColor: "#0a9551",
                                        fontWeight:'500'

                  }}
                  onClick={() => navigate("/admin/stock/add-vin")}
                >
                  Add Chassis
                </Button>
              </>
            )}
            {hasViewPermission && !isStockDashboardRedirect && (
              <Button
                icon={<HistoryOutlined />}
                type="primary"
                style={{
                  backgroundColor: "#0a9551",
                  borderColor: "#0a9551",
                                      fontWeight:'500'

                }}
                onClick={() => navigate("/admin/stock/history")}
              >
                Update History
              </Button>
            )}

            {hasViewPermission && !isStockDashboardRedirect && (
              <Button
                icon={<ExportOutlined />}
                style={{
                  backgroundColor: "#0a9551",
                  borderColor: "#0a9551",
                  color: "#fff",
                                      fontWeight:'500'

                }}
                onClick={handleExport}
                loading={isExporting}
              >
                Export
              </Button>
            )}
            {hasDeletePermission && !isStockDashboardRedirect && (
              <Button
                danger
                icon={
                  <DeleteOutlined
                    style={{
                                          fontWeight:'500',

                      color:
                        selectedRowKeys.length === 0
                          ? "#d9d9d9"
                          : "#ff4d4f",
                    }}
                  />
                }
                onClick={() => handleDelete()}
                disabled={selectedRowKeys.length === 0}
              >
                Delete
              </Button>
            )}
          </Flex>

          <Table
            size="middle"
            rowSelection={
              hasDeletePermission && !isStockDashboardRedirect
                ? rowSelection
                : null
            }
            columns={columns}
            dataSource={data}
            scroll={{ x: "max-content" }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
            onChange={(paginationData) => {
              const { current, pageSize } = paginationData;
              fetchStockData(current, pageSize, search, filters);
            }}
          />
        </Card>
      </div>

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        onSuccess={handleBulkUploadSuccess}
        onDownloadTemplate={handleDownloadTemplate}
      />

      {/* Bulk Upload Result Modal */}
      <BulkUploadResultModal
        isOpen={isResultOpen}
        onClose={handleCloseResultModal}
        results={uploadResults}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        onCancel={handleDeleteCancel}
        footer={[
          <Button
            key="cancel"
            onClick={handleDeleteCancel}
            disabled={isDeleting}
          >
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={handleDeleteConfirm}
            loading={isDeleting}
          >
            Confirm
          </Button>,
        ]}
        title="Delete VIN"
        width={500}
        maskClosable={false}
      >
        <Form form={deleteForm} layout="vertical">
          <div style={{ marginBottom: "16px" }}>
            <p>The selected VIN(s) will be deleted:</p>
            <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
              {selectedRowKeys.map((key) => {
                const item = data.find((d) => d.key === key);
                return <li key={key}>{item?.chassis_number}</li>;
              })}
            </ul>
          </div>
          <Form.Item
            name="reason"
            label="Reason"
            rules={[
              { required: true, message: "Please enter a reason for deletion" },
              { whitespace: true, message: "Reason cannot be empty" },
            ]}
          >
            <Input.TextArea
              placeholder="Enter reason for deletion"
              rows={3}
              className="form-control-ant"
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Result Modal */}
      <Modal
        open={isDeleteResultOpen}
        onCancel={handleDeleteResultClose}
        footer={[
          <Button key="close" type="primary" onClick={handleDeleteResultClose}>
            Close
          </Button>,
        ]}
        title="Delete VIN: Result"
        width={600}
      >
        <Table
          size="small"
          dataSource={deleteResults}
          columns={[
            { title: "Chassis Number", dataIndex: "vin", key: "vin" },
            {
              title: "Deletion Status",
              dataIndex: "status",
              key: "status",
              render: (status) => (
                <Tag color={status === "Success" ? "green" : "red"}>
                  {status}
                </Tag>
              ),
            },
            {
              title: "Failure Reason",
              dataIndex: "reason",
              key: "reason",
            },
          ]}
          pagination={false}
        />
      </Modal>
    </div>
  );
};

export default StockList;
